import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { workflowContractType } from '~/actions/workflow';
import { Select } from '~/eds/src/components/Select/Select';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { api } from '~/redux/api/createApi';
import { Workflow } from '~/types/Workflow';

import Section from '../Section';

export const ContractType = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { data, isLoading, isError } =
    api.endpoints.getWorkflowContractTypes.useQuery({});

  const workflow = useSelector(
    ({ workflow }: { workflow: Workflow }) => workflow,
  );
  const contractType = workflow.contractType;

  const dropdownOptions =
    data
      ?.filter((item) => !item.attributes?.linkedWorkflowId)
      .map((item) => ({
        label: item.attributes?.descriptor,
        value: item.attributes.id,
      })) ?? [];

  const contractTypeRequiredMessage = intl.formatMessage({
    id: 'ContractType.required',
    defaultMessage: 'Contract type is required',
    description: 'Error message when contract type is not selected',
  });

  const title = intl.formatMessage({
    id: 'Workflow.ContractType.sectionTitle',
    defaultMessage: 'Contract Type',
    description: 'WSS Contract Type',
  });

  const networkErrorMessage = intl.formatMessage({
    id: 'Workflow.ContractType.networkError',
    defaultMessage: 'Failed to load contract types',
    description: 'Error message when contract types cannot be loaded',
  });

  const errorMessage = !contractType ? contractTypeRequiredMessage : undefined;

  const handleContractTypeChange = useCallback(
    (value: string | undefined | null) => {
      dispatch(workflowContractType(data?.find(({ id }) => id === value)));
    },
    [data],
  );

  if (workflow?.published && !contractType) {
    return null;
  }

  return (
    <Section title={title}>
      {isError && (
        <StatusMessage
          autoDismiss={false}
          message={networkErrorMessage}
          status="danger"
        />
      )}
      <Select
        isMulti={false}
        name="contract-type-select"
        options={dropdownOptions}
        readOnly={workflow.published}
        value={contractType?.id}
        isLoading={isLoading}
        error={errorMessage}
        enableErrorMessage
        onChange={handleContractTypeChange}
      />
    </Section>
  );
};
